import { useEffect, useRef } from 'react';
import {
  Assets,
  Colors,
  FontFamily,
  Input,
  Main,
  Models,
  PrimaryButton,
  PublishedArProjectCard,
} from 'utils/imports.utils';
import { ButtonSize, ButtonTypes, ProjectStatus } from 'utils/interface.utils';
import { QRCodeCanvas } from 'qrcode.react';
import { canDownloadProjectImages, getProjectArLink, getProjectImagesFolderName, getQrLogoPlacement, getQuoteTargetsById, handlePayment, isPublishSuccessful, isUnlimitedUser, saveBytesAs, toastifyError, toastifySuccess, useSetState } from 'utils/functions.utils';
import { STRINGS } from 'constants/strings.constant';
import { loadRazorpayScript } from 'adapters/razorpay/razorpay.adapter';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { setProject } from 'store/reducers/projectSlice';
import PriceQuoteCard from 'components/price_quote_card/price_quote_card.component';
import PreviewPublishCard from 'components/preview_publish_card/preview_publish_card.component';
import PreviewStatusPill from 'components/preview_status_pill/preview_status_pill.component';
import PreviewStep from 'components/preview_step/preview_step.component';
import PreviewTargetFrame from 'components/preview_target_frame/preview_target_frame.component';
import { BreadcrumbItem } from 'interfaces/nav_bar.interface';
import { QuoteTargetInfo } from 'interfaces/payment_modal.interface';
import { PreviewPublishStatus } from 'interfaces/preview.interface';
import { QR_CODE_FILE_SUFFIX, QR_CODE_MIME_TYPE, QR_LOGO_HEIGHT, QR_LOGO_WIDTH, TARGET_QR_ERROR_CORRECTION_LEVEL, TARGET_QR_QUIET_ZONE_MODULES, TARGET_QR_SOURCE_SIZE_PX } from 'utils/constant.utils';
import './preview.screen.scss';


const Preview = () => {
  const [state, setState] = useSetState({
    isPublishedModalOpen: false,
    listOfTargetImages: [],
    listOfTargetImageIds: [],
    targetInfoById: {} as Record<string, QuoteTargetInfo>,
    isCurrentProjectNotPublished: false,
    projectName: '',
    projectStatus: '',
    projectPaymentStatus: false,
    isRazorpayLoaded: false,
    isPriceQuoteOpen: false,
    priceQuote: null as { amount: number; currency: string; target_breakdown: any[] } | null,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId } = useParams();
  // The code is drawn large so the downloaded file prints sharp; the stylesheet scales it down to its tile.
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const arLink = getProjectArLink(String(projectId));
  const { currentUser } = useSelector((state: RootState) => state.user);
  const currentProject = useSelector((state: RootState) => state.project.currentProject);
  // A target saved without an image has nothing to download; the carousel still gets the full list.
  const downloadableTargetImages: string[] = state.listOfTargetImages.filter(Boolean);
  // Same logo as the downloaded images; excavate clears the modules under it.
  const previewQrLogo = getQrLogoPlacement(TARGET_QR_SOURCE_SIZE_PX, QR_LOGO_WIDTH, QR_LOGO_HEIGHT);
  const isProjectLoaded = state.projectStatus !== '';
  const publishStatus: PreviewPublishStatus = !isProjectLoaded ? 'loading' : state.isCurrentProjectNotPublished ? 'draft' : 'published';
  const breadcrumbs: BreadcrumbItem[] = [
    { label: STRINGS.preview.breadcrumbProjects, path: '/welcome' },
    ...(state.projectName ? [{ label: state.projectName }] : []),
    { label: STRINGS.preview.breadcrumbPreview },
  ];

  // The editor opens the project the app has selected, which is not this one when the preview was reached by its link.
  const handleBackToEditor = () => {
    if (projectId && currentProject?.project_id !== projectId) {
      dispatch(setProject({ project_id: projectId, project_name: state.projectName }));
    }
    navigate('/editor');
  };

  const copyArLink = async () => {
    try {
      await navigator.clipboard.writeText(arLink);
      toastifySuccess(STRINGS.preview.linkCopied);
    } catch (error) {
      console.error('Failed to copy link:', error);
      toastifyError(STRINGS.preview.linkCopyFailed);
    }
  };

  const downloadQrCode = async () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, QR_CODE_MIME_TYPE));
      if (!blob) throw new Error('The QR code could not be turned into an image');
      saveBytesAs(new Uint8Array(await blob.arrayBuffer()), QR_CODE_MIME_TYPE, `${getProjectImagesFolderName(state.projectName)}${QR_CODE_FILE_SUFFIX}`);
    } catch (error) {
      console.error('Failed to download the QR code:', error);
      toastifyError(STRINGS.preview.qrDownloadFailed);
    }
  };

  // The exempt account (see isUnlimitedUser) is never asked to pay: the server publishes the project and marks
  // it paid, so there is no price quote and no checkout.
  const publishProjectFree = async () => {
    try {
      const res: unknown = await Models.auth.publishProject({ project_id: projectId });
      if (isPublishSuccessful(res)) {
        setState({ isPublishedModalOpen: true, isCurrentProjectNotPublished: false });
        toastifySuccess(STRINGS.project.publishSuccess);
      } else {
        toastifyError(STRINGS.project.publishFailed);
      }
    } catch (error) {
      console.error('Error publishing project:', error);
      toastifyError(STRINGS.project.publishFailed);
    }
  };

  const publishProject = async () => {
    if (!projectId || state.listOfTargetImageIds.length === 0) return;
    setState({ activity: true });
    if (isUnlimitedUser()) {
      await publishProjectFree();
      setState({ activity: false });
      return;
    }
    try {
      const quote: any = await Models.auth.getPriceQuote({
        project_id: projectId,
        target_ids: state.listOfTargetImageIds,
      });
      if (quote && quote.data) {
        setState({ priceQuote: quote.data, isPriceQuoteOpen: true });
      } else {
        toastifyError('Failed to fetch price quote');
      }
    } catch (error) {
      console.error('Error fetching price quote:', error);
      toastifyError('Failed to fetch price quote');
    } finally {
      setState({ activity: false });
    }
  };

  const confirmPublishProject = async () => {
    if (!projectId) return;
    setState({ activity: true });
    try {
      await handlePayment(currentUser, projectId, state.listOfTargetImageIds, state, setState);
    } catch (error) {
      console.error('Error creating payment order:', error);
    } finally {
      setState({ activity: false, isPriceQuoteOpen: false });
    }
  };

  const getAllProjectsData = async () => {
    if (!projectId) return;
    try {
        const res: any = await Models.auth.getManyProjectData({
            project_id: projectId,
        });
        if (res && res.data) {
          const targetImages = res.data.targets.map((target:any) => target.data.targetImage);
          // Real Target._id (set on each item as targetId once compile succeeds), not the
          // free-form editor id -- this is what the server's payment endpoints accept.
          const targetImageIds = res.data.targets
            .map((target:any) => target.data.targetId)
            .filter(Boolean);
          setState({ listOfTargetImages: targetImages, listOfTargetImageIds: targetImageIds, targetInfoById: getQuoteTargetsById(res.data.targets) })
        } else {
            console.error('No project data found');
        }
    } catch (error) {
        console.error('Error fetching project data:', error);
    }
  };

  const getCurrentProjectData = async () => {
    if (!projectId) return;
    try {
      const res: any = await Models.auth.getProject({ project_id: projectId });
      if (res && res.data) {
        setState({
          isCurrentProjectNotPublished: res.data.project_status !== ProjectStatus.PUBLISHED,
          projectName: res.data.project_name ?? '',
          projectStatus: res.data.project_status ?? '',
          projectPaymentStatus: res.data.payment_status === true,
        })
      } else {
        console.error('No project data found');
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  }

  useEffect(() => {
    let isMounted = true;
    const loadScript = async () => {
      const isLoaded = await loadRazorpayScript();
      if (isMounted && isLoaded) setState({ isRazorpayLoaded: true });
    };
    loadScript();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    getAllProjectsData();
    getCurrentProjectData();
  }, []);

  // The published modal opens when a payment made on this screen is verified. The server has
  // marked the project paid and published by then, so read it again to unlock the download.
  useEffect(() => {
    if (state.isPublishedModalOpen) {
      getCurrentProjectData();
    }
  }, [state.isPublishedModalOpen]);

  return (
    <Main
      backgroundColor={Colors.ink}
      isRenderOnlyLogo={false}
      breadcrumbs={breadcrumbs}>
      <div className="preview">
        <div className="preview_container">
          <header className="preview_header">
            <button type="button" className="preview_header_back" onClick={handleBackToEditor}>
              <img src={Assets.arrow_left} alt="" />
              {STRINGS.preview.backToEditor}
            </button>
            <div className="preview_header_title_row">
              <h1 className="preview_header_title">{STRINGS.preview.title}</h1>
              {isProjectLoaded && (
                <PreviewStatusPill
                  label={publishStatus === 'draft' ? STRINGS.preview.statusDraft : STRINGS.preview.statusLive}
                  isLive={publishStatus === 'published'}
                />
              )}
            </div>
            <p className="preview_header_subtitle">{STRINGS.preview.subtitle}</p>
          </header>
          <div className="preview_layout">
            <div className="preview_steps">
              <PreviewStep number={1} title={STRINGS.preview.stepOpenTitle} isPrimary={true}>
                <div className="preview_qr">
                  <div className="preview_qr_tile">
                    <QRCodeCanvas
                      ref={qrCanvasRef}
                      className="preview_qr_code"
                      value={arLink}
                      size={TARGET_QR_SOURCE_SIZE_PX}
                      marginSize={TARGET_QR_QUIET_ZONE_MODULES}
                      level={TARGET_QR_ERROR_CORRECTION_LEVEL}
                      title={STRINGS.preview.qrAlt}
                      style={{ width: '100%', height: '100%' }}
                      imageSettings={{ src: Assets.qr_logo, height: previewQrLogo.logoHeight, width: previewQrLogo.logoWidth, excavate: true }}
                    />
                  </div>
                  <p className="preview_qr_caption">{STRINGS.preview.stepOpenCaption}</p>
                </div>
                <div className="preview_link_divider">
                  <span>{STRINGS.preview.orCopyLink}</span>
                </div>
                <div className="preview_link">
                  <div className="preview_link_field">
                    <Input type="text" value={arLink} readOnly={true} />
                  </div>
                  <div className="preview_link_button">
                    <PrimaryButton
                      text={STRINGS.preview.copyLink}
                      left_icon={Assets.copy}
                      type={ButtonTypes.OUTLINED}
                      size={ButtonSize.LARGE}
                      fontFamily={FontFamily.semiBold}
                      iconSize={{ width: 18, height: 18 }}
                      color={Colors.primaryTextColor}
                      style={{ border: `1.5px solid ${Colors.primaryTextColor}` }}
                      onClick={copyArLink}
                    />
                  </div>
                </div>
              </PreviewStep>
              <PreviewStep number={2} title={STRINGS.preview.stepPointTitle}>
                <PreviewTargetFrame images={downloadableTargetImages} />
                <p className="preview_point_hint">{STRINGS.preview.stepPointHint}</p>
              </PreviewStep>
            </div>
            <PreviewPublishCard
              status={publishStatus}
              isPublishing={!!state.activity}
              onPublish={publishProject}
              isImagesDownloadEnabled={canDownloadProjectImages(state.projectPaymentStatus, state.projectStatus, downloadableTargetImages.length)}
              projectName={state.projectName}
              targetImages={downloadableTargetImages}
              arLink={arLink}
              onDownloadQr={downloadQrCode}
            />
          </div>
          {state.isPublishedModalOpen && (
            <PublishedArProjectCard
              isOpen={state.isPublishedModalOpen}
              onClose={() => setState({ isPublishedModalOpen: false })}
              projectId={projectId}
              onDownloadQr={downloadQrCode}
            />
          )}
          {state.isPriceQuoteOpen && state.priceQuote && (
            <PriceQuoteCard
              isOpen={state.isPriceQuoteOpen}
              onClose={() => setState({ isPriceQuoteOpen: false })}
              onConfirm={confirmPublishProject}
              amount={state.priceQuote.amount}
              currency={state.priceQuote.currency}
              targetBreakdown={state.priceQuote.target_breakdown}
              isConfirming={state.activity}
              projectName={state.projectName}
              targetInfo={state.targetInfoById}
            />
          )}
        </div>
      </div>
    </Main>
  );
};

export default Preview;
